import { Button, Flex, Input, Select, Tabs, Typography } from "antd";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { AiFillInfoCircle } from "react-icons/ai";
import { Group, Panel, Separator } from "react-resizable-panels";
import { Link } from "react-router";

import type { Endpoint, PathParams, RequestRecord } from "sandbox/types";

import { DataTable } from "design-system/components/DataTable";
import { trackAmplitudeEvent } from "sandbox/analytics/amplitude";
import { sandboxFetch } from "sandbox/api/client";
import { METHOD_COLORS, SANDBOX_API_BASE } from "sandbox/constants";
import { CopyButton } from "sandbox/features/shared";
import { useCredentials } from "sandbox/hooks/useCredentials";
import { sandboxPaths } from "sandbox/paths";
import { getClientToken } from "sandbox/utils/clientTokenCache";
import { getCredentialSecret } from "sandbox/utils/credentialStore";
import { getErrorStatus } from "shell/api/amplitudeCore";
import { AMP_MASK_CLASS } from "shell/api/amplitudePrivacy";

import { CodeSamples } from "./CodeSamples";
import { EndpointDocs } from "./EndpointDocs";
import s from "./RequestPane.module.css";
import { ResponsePanel } from "./ResponsePanel";

interface RequestPaneProps {
  endpoint: Endpoint;
  isHistorySidebarVisible: boolean;
  onResponse: (record: Omit<RequestRecord, "id" | "sentAt">) => void;
  onToggleHistorySidebar: () => void;
}

interface ResponseState {
  body: unknown;
  bytes: number;
  durationMs: number;
  raw: string;
  status: number;
}

const buildSpecPath = (
  endpoint: Endpoint,
  pathValues: Record<string, string>,
  queryValues: Record<string, string>,
) => {
  let path = endpoint.path;

  Object.entries(pathValues).forEach(([key, value]) => {
    if (value) {
      path = path.replace(`:${key}`, value);
    }
  });

  const query = Object.entries(queryValues)
    .filter(([, value]) => value)
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join("&");
  const specSuffix = query ? `?${query}` : "";
  return `${path}${specSuffix}`;
};

export const RequestPane = ({
  endpoint,
  onResponse,
  isHistorySidebarVisible,
  onToggleHistorySidebar,
}: RequestPaneProps) => {
  const { t } = useTranslation(["sandbox", "common"]);
  const { credentials } = useCredentials();
  const [selectedCredential, setSelectedCredential] = useState<string | null>(
    null,
  );
  const [pathValues, setPathValues] = useState<Record<string, string>>({});
  const [queryValues, setQueryValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (endpoint.queryParams ?? []).map(p => [p.name, p.example ?? ""]),
    ),
  );
  const [headerValues, setHeaderValues] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      (endpoint.headerParams ?? []).map(h => [h.name, h.example ?? ""]),
    ),
  );
  const [body, setBody] = useState<string>(
    endpoint.sampleBody ? JSON.stringify(endpoint.sampleBody, null, 2) : "",
  );
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<ResponseState | null>(null);
  const [activeFormat, setActiveFormat] = useState<"pretty" | "raw">("pretty");
  const [activeToken, setActiveToken] = useState<string | null>(null);

  useEffect(() => {
    if (credentials.length > 0) {
      setSelectedCredential(credentials[0].id);
    }
  }, [credentials]);

  const pathColumns = [
    {
      title: t("sandbox:labels.parameter"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("common:labels.value"),
      dataIndex: "pathValues",
      key: "pathValues",
      render: (_text: string, record: PathParams) => (
        <Input
          id={`pp-${record.name}`}
          value={pathValues[record.name] ?? ""}
          onChange={e =>
            setPathValues(prev => ({
              ...prev,
              [record.name]: e.target.value,
            }))
          }
        />
      ),
    },
    {
      title: t("common:labels.description"),
      dataIndex: "description",
      key: "description",
    },
  ];

  const paramsColumns = [
    {
      title: t("common:labels.key"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("labels.example"),
      dataIndex: "example",
      key: "example",
    },
    {
      title: t("common:labels.description"),
      dataIndex: "description",
      key: "description",
    },
  ];

  useEffect(() => {
    setPathValues(
      Object.fromEntries(
        (endpoint.pathParams ?? []).map(p => [p.name, p.example ?? ""]),
      ),
    );
    setQueryValues(
      Object.fromEntries(
        (endpoint.queryParams ?? []).map(p => [p.name, p.example ?? ""]),
      ),
    );
    setHeaderValues(
      Object.fromEntries(
        (endpoint.headerParams ?? []).map(h => [h.name, h.example ?? ""]),
      ),
    );
    setBody(
      endpoint.sampleBody ? JSON.stringify(endpoint.sampleBody, null, 2) : "",
    );
    setResponse(null);
    setActiveToken(null);
  }, [endpoint]);

  const specPath = buildSpecPath(endpoint, pathValues, queryValues);
  const displayUrl = `${SANDBOX_API_BASE}${specPath}`;
  const colors = METHOD_COLORS[endpoint.method];

  const handleSend = async () => {
    setSending(true);
    setResponse(null);

    const credentialId = selectedCredential;
    if (!credentialId) {
      setSending(false);
      return;
    }

    const credential = credentials.find(c => c.id === credentialId);
    if (!credential) {
      setSending(false);
      return;
    }

    try {
      const startTime = performance.now();

      const clientSecret = getCredentialSecret(credentialId);
      if (!clientSecret) {
        throw new Error(
          "Credential secret not found. Please re-create or rotate the credential.",
        );
      }
      const clientToken = await getClientToken({
        clientId: credential.clientId,
        clientSecret,
      });
      setActiveToken(clientToken);

      const response = await sandboxFetch(specPath, {
        method: endpoint.method,
        body: body ? (JSON.parse(body) as Record<string, unknown>) : undefined,
        headers: Object.fromEntries(
          Object.entries(headerValues).filter(([, value]) => value),
        ),
        auth: { mode: "authenticated" },
        authToken: clientToken,
      });

      const durationMs = Math.round(performance.now() - startTime);
      const raw = await response.text();
      const bytes = new Blob([raw]).size;
      const respBody = raw ? (JSON.parse(raw) as unknown) : null;

      const responseObj: ResponseState = {
        status: response.status,
        body: respBody,
        durationMs,
        bytes,
        raw,
      };
      setResponse(responseObj);
      setSending(false);
      trackAmplitudeEvent(`API Request Sent - ${endpoint.id}`, {
        outcome: response.ok ? "success" : "failure",
        error_status: response.ok ? undefined : response.status,
        endpoint_id: endpoint.id,
        method: endpoint.method,
        status: response.status,
        duration_ms: durationMs,
      });
      onResponse({
        method: endpoint.method,
        path: specPath,
        url: displayUrl,
        status: response.status,
        durationMs,
        bytes,
        endpointId: endpoint.id,
        endpointLabel: endpoint.label,
        requestBody: body || undefined,
        responseBody: respBody,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Request failed";
      const status = getErrorStatus(error) ?? 0;
      const raw = JSON.stringify({ error: message }, null, 2);
      const next: ResponseState = {
        status,
        body: { error: message },
        durationMs: 0,
        bytes: new Blob([raw]).size,
        raw,
      };
      setResponse(next);
      setSending(false);
      trackAmplitudeEvent(`API Request Sent - ${endpoint.id}`, {
        outcome: "failure",
        error_status: status,
        endpoint_id: endpoint.id,
        method: endpoint.method,
        status,
        duration_ms: 0,
      });
      onResponse({
        method: endpoint.method,
        path: specPath,
        url: displayUrl,
        status,
        durationMs: 0,
        bytes: new Blob([raw]).size,
        endpointId: endpoint.id,
        endpointLabel: endpoint.label,
        requestBody: body || undefined,
        responseBody: { error: message },
      });
    }
  };

  const tabs = [
    {
      key: "params",
      label: t("labels.parameters"),
      children: (
        <div className={s.tabBody}>
          {endpoint.queryParams && endpoint.queryParams.length > 0 ? (
            <DataTable
              columns={paramsColumns}
              data={endpoint.queryParams}
              bandedRows
            />
          ) : (
            <p className={s.empty}>
              {t("components.requestPane.noQueryParams")}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "path",
      label: t("common:labels.path"),
      children: (
        <div className={s.tabBody}>
          {endpoint.pathParams && endpoint.pathParams.length > 0 ? (
            <DataTable
              columns={pathColumns}
              data={endpoint.pathParams}
              bandedRows
            />
          ) : (
            <p className={s.empty}>
              {t("components.requestPane.noPathParams")}
            </p>
          )}
        </div>
      ),
    },
    {
      key: "headers",
      label: t("labels.headers"),
      children: (
        <div className={s.tabBody}>
          <table className={s.kvTable}>
            <thead>
              <tr>
                <th>{t("common:labels.key")}</th>
                <th>{t("common:labels.value")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={s.mono}>Authorization</td>
                <td className={clsx(s.mono, AMP_MASK_CLASS)}>
                  {activeToken ? (
                    <>
                      Bearer {activeToken.slice(0, 30)}...
                      <CopyButton
                        value={`Bearer ${activeToken}`}
                        label={t("components.requestPane.copyHeader")}
                      />
                    </>
                  ) : (
                    t("components.requestPane.headersTable.authorization")
                  )}
                </td>
              </tr>
              <tr>
                <td className={s.mono}>Content-Type</td>
                <td className={s.mono}>
                  {t("components.requestPane.headersTable.contentType")}
                </td>
              </tr>
              {(endpoint.headerParams ?? []).map(h => (
                <tr key={h.name}>
                  <td className={s.mono}>
                    {h.name}
                    {h.required ? " *" : ""}
                  </td>
                  <td>
                    <Input
                      id={`hp-${h.name}`}
                      data-testid={`header-input-${h.name}`}
                      value={headerValues[h.name] ?? ""}
                      placeholder={h.description}
                      onChange={e =>
                        setHeaderValues(prev => ({
                          ...prev,
                          [h.name]: e.target.value,
                        }))
                      }
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
    },
    {
      key: "auth",
      label: t("labels.auth"),
      children: (
        <div className={s.tabBody}>
          {credentials.length > 0 && (
            <Flex align="flex-start" gap="middle" className={s.authCard}>
              <span
                className={clsx(
                  s.authBadge,
                  !selectedCredential && s.authBadgeUnauthenticated,
                )}
              >
                {selectedCredential
                  ? t("components.requestPane.authenticated")
                  : t("components.requestPane.unauthenticated")}
              </span>
              <div>
                <p className={s.authDesc}>
                  {selectedCredential
                    ? t("components.requestPane.authDescription")
                    : t("components.requestPane.noCredential")}
                </p>
                {activeToken && (
                  <div className={s.authTokenRow}>
                    <Flex align="center" gap="small">
                      <code className={clsx(s.tokenChip, AMP_MASK_CLASS)}>
                        {activeToken.slice(0, 25)}...
                      </code>
                      <CopyButton
                        value={activeToken}
                        label={t("components.requestPane.copyToken")}
                        size="middle"
                      />
                    </Flex>
                  </div>
                )}
              </div>
            </Flex>
          )}
          {credentials.length === 0 ? (
            <Flex align="center" gap="small" className={s.authEmptyInfo}>
              <AiFillInfoCircle className={s.infoIcon} aria-hidden />
              <div>
                <p className={s.authEmptyTitle}>
                  {t("components.requestPane.noCredentials.title")}
                </p>
                <p className={s.authEmptyDescription}>
                  <Trans
                    ns="sandbox"
                    i18nKey="components.requestPane.noCredentials.description"
                    components={{
                      credentialsLink: <Link to={sandboxPaths.credentials} />,
                    }}
                  />
                </p>
              </div>
            </Flex>
          ) : (
            <div className={s.credentialSelectWrap}>
              <p className={clsx(s.mono, s.credentialSelectLabel)}>
                {t("labels.credentials")}
              </p>
              <Select
                data-testid="credential-select"
                style={{ width: "100%" }}
                placeholder={t(
                  "components.requestPane.selectCredentialPlaceholder",
                )}
                allowClear
                value={selectedCredential}
                onChange={setSelectedCredential}
                options={credentials.map(c => ({
                  value: c.id,
                  label: `${c.name} (${c.clientId})`,
                }))}
              />
            </div>
          )}
        </div>
      ),
    },
    ...(endpoint.method === "POST" ||
    endpoint.method === "PUT" ||
    endpoint.method === "PATCH"
      ? [
          {
            key: "body",
            label: t("labels.body"),
            children: (
              <div className={s.tabBody}>
                <Input.TextArea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  className={s.bodyEditor}
                  autoSize={{ minRows: 8, maxRows: 18 }}
                />
              </div>
            ),
          },
        ]
      : []),
    {
      key: "code",
      label: t("common:labels.code"),
      children: (
        <div className={s.tabBody}>
          <CodeSamples
            endpoint={endpoint}
            url={displayUrl}
            body={body || undefined}
            bearerToken={activeToken ?? undefined}
          />
        </div>
      ),
    },
    {
      key: "docs",
      label: t("labels.docs"),
      children: (
        <div className={s.tabBody}>
          <EndpointDocs endpoint={endpoint} />
        </div>
      ),
    },
  ];

  return (
    <div className={s.root}>
      <Group orientation="vertical" className={s.group}>
        <Panel className={s.topPanel}>
          <header className={s.header}>
            <div className={s.headerRow}>
              <Typography.Title level={3} className={s.title}>
                {endpoint.label}
              </Typography.Title>
              <Button
                onClick={onToggleHistorySidebar}
                data-testid="recent-requests-button"
              >
                {isHistorySidebarVisible
                  ? t("components.recentRequests.hide")
                  : t("components.recentRequests.show")}
              </Button>
            </div>
          </header>

          <Flex align="stretch" gap="small" className={s.urlBar}>
            <span
              className={s.methodChip}
              style={{
                color: colors.fg,
                background: colors.bg,
                borderColor: colors.border,
              }}
            >
              {endpoint.method}
            </span>
            <Input
              className={s.urlInput}
              prefix={
                <span className={s.urlInputPrefix}>
                  {displayUrl.replace(specPath, "")}
                </span>
              }
              value={specPath}
              readOnly
            />
            <Button
              type="primary"
              size="medium"
              loading={sending}
              disabled={!selectedCredential}
              onClick={handleSend}
              className={s.sendButton}
            >
              {sending ? t("actions.sending") : t("actions.send")}
            </Button>
          </Flex>

          <Tabs items={tabs} defaultActiveKey="params" className={s.tabs} />
        </Panel>
        <Separator className={s.resizeHandle} />
        <Panel minSize="150px" maxSize="600px" defaultSize="300px">
          <ResponsePanel
            response={response}
            sending={sending}
            activeFormat={activeFormat}
            onFormatChange={setActiveFormat}
          />
        </Panel>
      </Group>
    </div>
  );
};
